import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Heart, Shield, FileText, Calendar, AlertTriangle, CheckCircle, Eye, Download, RefreshCw, Search, ArrowUpDown, Clock } from 'lucide-react';
import { Modal, EmptyState, Tooltip } from '../Common/ReusableUI';
import { TableSkeleton, CardSkeleton } from '../Common/SkeletonLoaders';
import { useToast } from '../Common/ToastProvider';

function relativeTime(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function freshnessBand(iso) {
  const hrs = (Date.now() - new Date(iso).getTime()) / 3600000;
  if (hrs > 168) return 'stale';
  if (hrs > 24) return 'warn';
  return 'fresh';
}

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
  { value: 'date-desc', label: 'Newest first' },
  { value: 'date-asc', label: 'Oldest first' },
];

function useSortedFiltered(items, searchQuery, sortKey, nameField, dateField) {
  return useMemo(() => {
    const q = searchQuery.toLowerCase();
    let filtered = items;
    if (q) {
      filtered = items.filter(item => {
        const name = (item[nameField] || '').toLowerCase();
        const extra = Object.values(item).filter(v => typeof v === 'string').join(' ').toLowerCase();
        return name.includes(q) || extra.includes(q);
      });
    }
    const sorted = [...filtered];
    const [field, dir] = sortKey.split('-');
    sorted.sort((a, b) => {
      if (field === 'name') {
        const cmp = (a[nameField] || '').localeCompare(b[nameField] || '');
        return dir === 'asc' ? cmp : -cmp;
      }
      const dateA = new Date(a[dateField] || 0).getTime();
      const dateB = new Date(b[dateField] || 0).getTime();
      return dir === 'asc' ? dateA - dateB : dateB - dateA;
    });
    return sorted;
  }, [items, searchQuery, sortKey, nameField, dateField]);
}

const SortDropdown = ({ value, onChange }) => (
  <div className="relative inline-flex items-center">
    <ArrowUpDown className="w-4 h-4 text-gray-400 absolute left-3 pointer-events-none" />
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
      aria-label="Sort order"
    >
      {SORT_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const TabSearchBar = ({ value, onChange, placeholder }) => (
  <div className="relative flex-1">
    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
    />
  </div>
);

const MyHealthRecord = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(new Date().toISOString());
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { showToast } = useToast();

  const [medSearch, setMedSearch] = useState('');
  const [allergySearch, setAllergySearch] = useState('');
  const [conditionSearch, setConditionSearch] = useState('');
  const [immunizationSearch, setImmunizationSearch] = useState('');
  const [labSearch, setLabSearch] = useState('');

  const [medSort, setMedSort] = useState('name-asc');
  const [allergySort, setAllergySort] = useState('date-desc');
  const [conditionSort, setConditionSort] = useState('name-asc');
  const [immunizationSort, setImmunizationSort] = useState('date-desc');
  const [labSort, setLabSort] = useState('date-desc');

  const healthRecordData = {
    patient: {
      name: 'Margaret Johnson',
      dob: '1945-03-15',
      medicare: '1234 56789 0',
      ihi: '8003608000001234',
      lastUpdated: '2025-01-24 14:30'
    },
    medications: [
      { id: 'MHR001', name: 'Metformin 500mg', dosage: '1 tablet twice daily', startDate: '2024-06-15', prescriber: 'Dr. Sarah Mitchell', status: 'Active', pbsCode: 'PBS-001', lastUpdated: '2025-01-20' },
      { id: 'MHR002', name: 'Lisinopril 10mg', dosage: '1 tablet daily', startDate: '2024-08-10', prescriber: 'Dr. Robert Smith', status: 'Active', pbsCode: 'PBS-002', lastUpdated: '2025-01-18' },
      { id: 'MHR003', name: 'Atorvastatin 20mg', dosage: '1 tablet at bedtime', startDate: '2024-09-05', prescriber: 'Dr. Sarah Mitchell', status: 'Active', pbsCode: 'PBS-003', lastUpdated: '2025-01-22' },
      { id: 'MHR004', name: 'Warfarin 5mg', dosage: '1 tablet daily', startDate: '2024-11-12', prescriber: 'Dr. Emily Wilson', status: 'Active', pbsCode: 'PBS-004', lastUpdated: '2025-01-24' }
    ],
    allergies: [
      { id: 'ALL001', allergen: 'Penicillin', reaction: 'Rash, difficulty breathing', severity: 'Severe', reportedDate: '2020-05-10', reporter: 'Dr. John Brown' },
      { id: 'ALL002', allergen: 'Sulfonamides', reaction: 'Mild rash', severity: 'Moderate', reportedDate: '2021-08-15', reporter: 'Dr. Sarah Mitchell' }
    ],
    conditions: [
      { id: 'CON001', condition: 'Type 2 Diabetes', status: 'Active', onsetDate: '2020-03-15', lastReviewed: '2025-01-15', provider: 'Dr. Sarah Mitchell' },
      { id: 'CON002', condition: 'Hypertension', status: 'Active', onsetDate: '2019-11-20', lastReviewed: '2025-01-10', provider: 'Dr. Robert Smith' },
      { id: 'CON003', condition: 'Atrial Fibrillation', status: 'Active', onsetDate: '2024-10-05', lastReviewed: '2025-01-20', provider: 'Dr. Emily Wilson' }
    ],
    immunizations: [
      { id: 'IMM001', vaccine: 'COVID-19 (Pfizer)', date: '2021-03-15', provider: 'Sunshine Medical Centre', lotNumber: 'PF123456', nextDue: null },
      { id: 'IMM002', vaccine: 'Influenza 2024', date: '2024-04-10', provider: 'Sunshine Medical Centre', lotNumber: 'FLU2024001', nextDue: '2025-04-10' },
      { id: 'IMM003', vaccine: 'Pneumococcal (PPSV23)', date: '2023-06-20', provider: 'Sunshine Medical Centre', lotNumber: 'PNEU2023001', nextDue: '2028-06-20' }
    ],
    labResults: [
      { id: 'LAB001', test: 'HbA1c', value: '7.2%', unit: '%', referenceRange: '< 7.0%', status: 'High', date: '2025-01-15', provider: 'Pathology Lab' },
      { id: 'LAB002', test: 'Creatinine', value: '1.1', unit: 'mg/dL', referenceRange: '0.6 - 1.2', status: 'Normal', date: '2025-01-15', provider: 'Pathology Lab' },
      { id: 'LAB003', test: 'INR', value: '2.8', unit: 'ratio', referenceRange: '2.0 - 3.0', status: 'Normal', date: '2025-01-20', provider: 'Pathology Lab' }
    ]
  };

  const handleSync = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const success = Math.random() > 0.05;
      setLoading(false);
      if (success) {
        setLastSync(new Date().toISOString());
        showToast('My Health Record synced successfully', 'success');
      } else {
        showToast('Sync failed — check network and retry', 'error', 6000);
      }
    }, 2000);
  }, [showToast]);

  useEffect(() => {
    handleSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'High': return 'bg-red-100 text-red-800';
      case 'Normal': return 'bg-green-100 text-green-800';
      case 'Severe': return 'bg-red-100 text-red-800';
      case 'Moderate': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredMeds = useSortedFiltered(healthRecordData.medications, medSearch, medSort, 'name', 'startDate');
  const filteredAllergies = useSortedFiltered(healthRecordData.allergies, allergySearch, allergySort, 'allergen', 'reportedDate');
  const filteredConditions = useSortedFiltered(healthRecordData.conditions, conditionSearch, conditionSort, 'condition', 'onsetDate');
  const filteredImmunizations = useSortedFiltered(healthRecordData.immunizations, immunizationSearch, immunizationSort, 'vaccine', 'date');
  const filteredLabs = useSortedFiltered(healthRecordData.labResults, labSearch, labSort, 'test', 'date');

  const band = freshnessBand(lastSync);

  const renderDetailModal = () => {
    if (!selectedRecord) return null;
    const r = selectedRecord;
    return (
      <Modal isOpen={!!selectedRecord} onClose={() => setSelectedRecord(null)} title={r.name || r.allergen || r.condition || r.vaccine || r.test || 'Record Details'} size="md">
        <div className="space-y-3 text-sm">
          {Object.entries(r).filter(([k]) => k !== 'id').map(([key, val]) => (
            <div key={key} className="flex justify-between border-b border-gray-100 pb-2">
              <span className="font-medium text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
              <span className="text-gray-900">{val || '—'}</span>
            </div>
          ))}
        </div>
      </Modal>
    );
  };

  const ViewButton = ({ record }) => (
    <button
      onClick={() => setSelectedRecord(record)}
      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
      aria-label="View details"
    >
      <Eye className="w-4 h-4" />
    </button>
  );

  const renderEmptyState = (tabName) => (
    <EmptyState
      icon={FileText}
      title={`No ${tabName} found`}
      description="Try adjusting your search or sync to pull fresh data from My Health Record."
    />
  );

  return (
    <div className="space-y-6">
      {/* Data Freshness Banner */}
      {band === 'stale' ? (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm font-medium" role="alert">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          Data is stale — please sync now &middot; Last synced: {relativeTime(lastSync)}
        </div>
      ) : band === 'warn' ? (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium" role="alert">
          <Clock className="w-4 h-4 flex-shrink-0" />
          Data may be outdated — sync recommended &middot; Last synced: {relativeTime(lastSync)}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          Last synced: {relativeTime(lastSync)}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              My Health Record Integration
              <Heart className="w-8 h-8 text-blue-200" />
            </h1>
            <p className="mt-2 opacity-90">
              Secure access to patient health information from My Health Record
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSync}
              disabled={loading}
              className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Syncing...' : 'Sync Now'}
            </button>
            <button onClick={onClose} className="text-white hover:text-blue-200 text-2xl">×</button>
          </div>
        </div>
      </div>

      {/* Patient Info with Tooltips */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">{healthRecordData.patient.name}</h3>
            <p className="text-sm text-gray-600">
              DOB: {healthRecordData.patient.dob} &bull;{' '}
              <Tooltip content="Medicare card number used for PBS and MBS claims" position="bottom">
                <span className="underline decoration-dotted cursor-help">Medicare: {healthRecordData.patient.medicare}</span>
              </Tooltip>
            </p>
            <p className="text-xs text-gray-500">
              <Tooltip content="Individual Healthcare Identifier — 16-digit unique national patient ID" position="bottom">
                <span className="underline decoration-dotted cursor-help">IHI: {healthRecordData.patient.ihi}</span>
              </Tooltip>
              {' '}&bull; Last updated: {healthRecordData.patient.lastUpdated}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-green-600">
          <CheckCircle className="w-4 h-4" />
          <span>Connected to My Health Record</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6 py-2 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: FileText },
              { id: 'medications', label: 'Medications', icon: Heart },
              { id: 'allergies', label: 'Allergies', icon: AlertTriangle },
              { id: 'conditions', label: 'Conditions', icon: FileText },
              { id: 'immunizations', label: 'Immunizations', icon: Shield },
              { id: 'labresults', label: 'Lab Results', icon: Calendar }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50 rounded-t-lg'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 rounded-t-lg'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Skeleton state while syncing */}
          {loading && activeTab !== 'overview' ? (
            <div className="space-y-4">
              <CardSkeleton />
              <TableSkeleton rows={3} columns={4} />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <CardSkeleton /><CardSkeleton /><CardSkeleton />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <Heart className="w-6 h-6 text-blue-600" />
                            <div>
                              <h4 className="font-semibold text-blue-800">Active Medications</h4>
                              <p className="text-2xl font-bold text-blue-600">{healthRecordData.medications.length}</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6 text-red-600" />
                            <div>
                              <h4 className="font-semibold text-red-800">Allergies</h4>
                              <p className="text-2xl font-bold text-red-600">{healthRecordData.allergies.length}</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                            <div>
                              <h4 className="font-semibold text-green-800">Conditions</h4>
                              <p className="text-2xl font-bold text-green-600">{healthRecordData.conditions.length}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-800 mb-3">Recent Activity</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Last sync with My Health Record:</span><span className="font-medium">{new Date(lastSync).toLocaleString()}</span></div>
                          <div className="flex justify-between"><span>Medication records updated:</span><span className="font-medium">{healthRecordData.medications.length} items</span></div>
                          <div className="flex justify-between"><span>Allergy records updated:</span><span className="font-medium">{healthRecordData.allergies.length} items</span></div>
                          <div className="flex justify-between"><span>Lab results available:</span><span className="font-medium">{healthRecordData.labResults.length} results</span></div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Medications Tab */}
              {activeTab === 'medications' && (
                <div className="space-y-4">
                  <div className="flex gap-3 flex-wrap">
                    <TabSearchBar value={medSearch} onChange={setMedSearch} placeholder="Search medications..." />
                    <SortDropdown value={medSort} onChange={setMedSort} />
                    <button
                      onClick={() => showToast('Export started — file will download shortly', 'info')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
                    >
                      <Download className="w-4 h-4" /> Export
                    </button>
                  </div>
                  {filteredMeds.length === 0 ? renderEmptyState('medications') : (
                    <div className="space-y-3">
                      {filteredMeds.map(med => (
                        <div key={med.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-800">{med.name}</h4>
                              <p className="text-sm text-gray-600 mb-2">{med.dosage}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                                <span>Prescriber: {med.prescriber}</span>
                                <span>Started: {med.startDate}</span>
                                <Tooltip content="Pharmaceutical Benefits Scheme subsidy code" position="top">
                                  <span className="underline decoration-dotted cursor-help">PBS: {med.pbsCode}</span>
                                </Tooltip>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(med.status)}`}>{med.status}</span>
                              <ViewButton record={med} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Allergies Tab */}
              {activeTab === 'allergies' && (
                <div className="space-y-4">
                  <div className="flex gap-3 flex-wrap">
                    <TabSearchBar value={allergySearch} onChange={setAllergySearch} placeholder="Search allergies..." />
                    <SortDropdown value={allergySort} onChange={setAllergySort} />
                  </div>
                  {filteredAllergies.length === 0 ? renderEmptyState('allergies') : (
                    <div className="space-y-3">
                      {filteredAllergies.map(allergy => (
                        <div key={allergy.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-red-800">{allergy.allergen}</h4>
                              <Tooltip content="Documented adverse reaction to this allergen" position="top">
                                <p className="text-sm text-red-700 mb-2 cursor-help">{allergy.reaction}</p>
                              </Tooltip>
                              <div className="flex items-center gap-4 text-sm text-red-600">
                                <span>Severity: {allergy.severity}</span>
                                <span>Reported: {allergy.reportedDate}</span>
                                <span>By: {allergy.reporter}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(allergy.severity)}`}>{allergy.severity}</span>
                              <ViewButton record={allergy} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Conditions Tab */}
              {activeTab === 'conditions' && (
                <div className="space-y-4">
                  <div className="flex gap-3 flex-wrap">
                    <TabSearchBar value={conditionSearch} onChange={setConditionSearch} placeholder="Search conditions..." />
                    <SortDropdown value={conditionSort} onChange={setConditionSort} />
                  </div>
                  {filteredConditions.length === 0 ? renderEmptyState('conditions') : (
                    <div className="space-y-3">
                      {filteredConditions.map(cond => (
                        <div key={cond.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <Tooltip content="ICD-10-AM coded diagnosis from clinical record" position="top">
                                <h4 className="font-semibold text-gray-800 cursor-help">{cond.condition}</h4>
                              </Tooltip>
                              <p className="text-sm text-gray-600 mb-2">Status: {cond.status}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>Onset: {cond.onsetDate}</span>
                                <span>Last reviewed: {cond.lastReviewed}</span>
                                <span>Provider: {cond.provider}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(cond.status)}`}>{cond.status}</span>
                              <ViewButton record={cond} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Immunizations Tab */}
              {activeTab === 'immunizations' && (
                <div className="space-y-4">
                  <div className="flex gap-3 flex-wrap">
                    <TabSearchBar value={immunizationSearch} onChange={setImmunizationSearch} placeholder="Search immunizations..." />
                    <SortDropdown value={immunizationSort} onChange={setImmunizationSort} />
                  </div>
                  {filteredImmunizations.length === 0 ? renderEmptyState('immunizations') : (
                    <div className="space-y-3">
                      {filteredImmunizations.map(imm => (
                        <div key={imm.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-800">{imm.vaccine}</h4>
                              <p className="text-sm text-gray-600 mb-2">Date: {imm.date}</p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>Provider: {imm.provider}</span>
                                <span>Lot: {imm.lotNumber}</span>
                                {imm.nextDue && <span className="text-amber-600 font-medium">Next due: {imm.nextDue}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <div className="text-sm text-gray-600">Administered</div>
                                <div className="text-xs text-gray-500">{imm.date}</div>
                              </div>
                              <ViewButton record={imm} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Lab Results Tab */}
              {activeTab === 'labresults' && (
                <div className="space-y-4">
                  <div className="flex gap-3 flex-wrap">
                    <TabSearchBar value={labSearch} onChange={setLabSearch} placeholder="Search lab results..." />
                    <SortDropdown value={labSort} onChange={setLabSort} />
                  </div>
                  {filteredLabs.length === 0 ? renderEmptyState('lab results') : (
                    <div className="space-y-3">
                      {filteredLabs.map(result => (
                        <div key={result.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <Tooltip content="Pathology test from accredited laboratory" position="top">
                                <h4 className="font-semibold text-gray-800 cursor-help">{result.test}</h4>
                              </Tooltip>
                              <p className="text-sm text-gray-600 mb-2">
                                Value: {result.value} {result.unit} (Reference: {result.referenceRange})
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>Date: {result.date}</span>
                                <span>Provider: {result.provider}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(result.status)}`}>{result.status}</span>
                              <ViewButton record={result} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {renderDetailModal()}
    </div>
  );
};

export default MyHealthRecord;
